<template>
  <div class="tu-scope" :class="{ 'page-section': !embedded }">
    <div :class="embedded ? '' : containerClass">
      <h2 class="tu-page-title mb-2" v-if="!embedded">Paths</h2>
      <p class="mb-4" v-if="!embedded">Start where you are and work toward a nursing degree, with the programs, prerequisites and applications mapped.</p>

      <div class="tu-toolbar" role="search" aria-label="Filter paths">
        <tu-filter-search
          :value="searchTerm"
          placeholder="Search a goal or credential"
          label="Search paths by goal or credential"
          @input="onSearchInput"
        />
        <template v-if="selectedList !== 'mine'">
          <tu-filter-select
            :value="selectedUniversitySlug"
            label="School"
            placeholder="All schools"
            :options="universityOptions"
            aria-label="Filter paths by school"
            @change="selectedUniversitySlug = $event; onFilterChange()"
          />
          <tu-filter-select
            :value="selectedState"
            label="State"
            placeholder="All states"
            :options="stateOptions"
            aria-label="Filter paths by state"
            @change="selectedState = $event; onFilterChange()"
          />
        </template>
      </div>

      <div class="tu-results">
        <p class="tu-count" role="status" aria-live="polite">
          <template v-if="isLoading">Loading paths…</template>
          <template v-else-if="totalPathways">
            Showing {{ pageFromCount }}–{{ pageToCount }} of {{ totalPathways }}
            {{ selectedList === 'mine' ? 'enrolled paths' : 'paths' }}
          </template>
        </p>
        <div class="tu-results-tools">
          <button v-if="hasActiveFilters" type="button" class="tu-clear" @click="clearFilters">Clear filters <span aria-hidden="true">×</span></button>
          <radio-button-group
            :options="listFilterOptions"
            :primary-color="brandColor"
            name="list-pathways"
            :model-value="selectedList"
            @update:modelValue="selectedList = $event"
          />
        </div>
      </div>

      <div v-if="isLoading" class="tu-card-grid" aria-busy="true">
        <div v-for="i in 6" :key="`s-${i}`" class="tu-skel"></div>
      </div>

      <div v-else-if="!totalPathways" class="tu-empty">
        <md-icon>directions</md-icon>
        <h4 class="tu-empty-title">{{ selectedList === 'mine' ? 'No paths yet' : 'Let’s widen your search' }}</h4>
        <p class="tu-empty-text" v-if="selectedList === 'mine'">
          You're not enrolled in any paths yet. Paths are added when you enroll through your school.
        </p>
        <p class="tu-empty-text" v-else>Try another credential or remove a filter to see more ways forward.</p>
        <button v-if="selectedList === 'mine'" type="button" class="tu-btn tu-btn--primary tu-empty-cta" @click="selectedList = 'all'">
          Browse all paths
        </button>
        <button v-else-if="hasActiveFilters" type="button" class="tu-btn tu-btn--ghost tu-empty-cta" @click="clearFilters">
          Clear filters
        </button>
      </div>

      <div v-else class="tu-card-grid" :class="isMobSmallScreen ? 'mb-5' : ''">
        <pathway-card
          v-for="pathway in pathways"
          :key="`pw-${pathway.id}`"
          :pathway="pathway"
          :enrolled="isEnrolled(pathway.id)"
          :schools="programCountFor(pathway)"
          @view="viewPathway"
        />
      </div>

      <pagination
        v-if="totalPathways > pathways.length"
        v-model="currentPage"
        :total-rows="totalPathways"
        :per-page="perPage"
        class="mt-4"
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { UsaStates } from 'usa-states';
import { mapActions, mapGetters } from 'vuex';

import Page from '@/components/Page.vue';
import RadioButtonGroup from '@/components/RadioGroup.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import TuFilterSearch from '@/components/Ui/TuFilterSearch.vue';
import TuFilterSelect from '@/components/Ui/TuFilterSelect.vue';
import PathwayCard from './PathwayCard.vue';

function pathwayRank(p) {
  const t = String((p && p.title) || '');
  if (/^CNA to ADN/i.test(t)) return 0;
  if (/dental/i.test(t)) return 1;
  if (/^CNA\b/i.test(t)) return 2;
  return 3;
}

export default {
  name: 'PathwaysList',
  components: { PathwayCard, Pagination, RadioButtonGroup, TuFilterSearch, TuFilterSelect },
  extends: Page,

  // When embedded (e.g. as the CareCampus home), drop the page wrapper + heading
  // so it doesn't duplicate the dashboard welcome.
  props: {
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      programCounts: {},
      title: 'Paths',
      isLoading: true,
      perPage: 24,
      currentPage: 1,
      totalPathways: 0,
      pageFromCount: 0,
      pageToCount: 0,
      pathways: [],
      enrolledIds: new Set(),
      searchTerm: '',
      // Empty until mounted so assigning the route's list always fires the watcher (first fetch).
      selectedList: '',
      selectedUniversitySlug: '',
      selectedState: '',
      universityOptions: [],
      windowWidth: window.innerWidth,
      onFirstLoad: true,
      listFilterOptions: [
        { text: 'My Paths', value: 'mine' },
        { text: 'Paths', value: 'all' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    brandColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color') || '#3b86f5';
    },
    isMobSmallScreen() {
      return this.windowWidth <= 776;
    },

    stateOptions() {
      return new UsaStates().states.map(s => ({ value: s.name, text: s.name }));
    },

    hasActiveFilters() {
      return !!(this.searchTerm || this.selectedUniversitySlug || this.selectedState);
    },
  },

  watch: {
    selectedList: {
      handler() {
        this.searchTerm = '';
        // A school preselected via ?uni= (e.g. from a locked school page) survives the first load.
        this.selectedUniversitySlug = this.onFirstLoad ? this.$route.query.uni || '' : '';
        this.onFirstLoad = false;
        this.selectedState = '';
        this.fetchPathways();
        if (this.$route.query.list !== this.selectedList) {
          this.$router.replace({
            query: { list: this.selectedList, ...(this.selectedUniversitySlug && { uni: this.selectedUniversitySlug }) },
          });
        }
      },
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getAllPathways', 'getMyPathwayEnrollments']),
    ...mapActions('uni', ['getAllUniversities', 'getAllUniversitiesByState']),

    /** Programs a path leads to: the directory count for its destination program, scoped to the path's state and school tags. */
    programCountFor(p) {
      if (!p) return null;
      const n = this.programCounts[p.id];
      if (n != null) return n;
      const uops = get(p, 'uni_offered_programs');
      return Array.isArray(uops) && uops.length ? uops.length : null;
    },
    async loadProgramCounts(list) {
      await Promise.all(
        (list || []).map(async p => {
          const pid = get(p, 'to_program.id');
          if (!pid || this.programCounts[p.id] != null) return;
          try {
            const tags = (p.destination_designations || []).join(',');
            const res = await this.getAllUniversitiesByState({
              program_id: pid, basic: true, limit: 1,
              ...(p.state ? { state: p.state } : {}),
              ...(tags ? { designation_all: tags } : {}),
            });
            const count = get(res, 'data.count', get(res, 'count', null));
            if (count != null) this.$set(this.programCounts, p.id, Number(count));
          } catch (_err) {
            /* the card falls back to attached programs */
          }
        }),
      );
    },

    onFilterChange() {
      this.fetchPathways();
    },

    clearFilters() {
      this.searchTerm = '';
      this.selectedUniversitySlug = '';
      this.selectedState = '';
      this.fetchPathways();
    },

    async fetchUniversityOptions() {
      try {
        const res = await this.getAllUniversities({ ordering: 'name', limit: 200, basic: true });
        const list = get(res, 'data.results', get(res, 'data', [])) || [];
        this.universityOptions = list
          .filter(u => u && u.slug)
          .map(u => ({ value: u.slug, text: u.name }));
      } catch (_err) {
        this.universityOptions = [];
      }
    },

    isEnrolled(pathwayId) {
      return this.enrolledIds.has(pathwayId);
    },

    sortPathways(list) {
      return (list || []).slice().sort((a, b) => {
        const r = pathwayRank(a) - pathwayRank(b);
        if (r !== 0) return r;
        return String(a.title || '').localeCompare(String(b.title || ''));
      });
    },

    async fetchEnrolledIds() {
      try {
        const response = await this.getMyPathwayEnrollments({ limit: 200 });
        const ids = get(response, 'data.results', [])
          .map(e => get(e, 'pathway.id'))
          .filter(Boolean);
        this.enrolledIds = new Set(ids);
      } catch (_err) {
        this.enrolledIds = new Set();
      }
    },

    async fetchPathways(pageNum = 1) {
      if (this.selectedList === 'mine') {
        await this.fetchMyPathways(pageNum);
      } else {
        await this.fetchAllPathways(pageNum);
      }
    },

    async fetchAllPathways(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllPathways({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.selectedUniversitySlug && {
            uni_offered_programs__university__slug: this.selectedUniversitySlug,
          }),
          ...(this.selectedState && { state: this.selectedState }),
        });
        this.pathways = this.sortPathways(get(response, 'data.results', []));
        this.loadProgramCounts(this.pathways);
        this.currentPage = pageNum;
        this.totalPathways = get(response, 'data.count', 0);
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + this.pathways.length - 1;
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchMyPathways(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getMyPathwayEnrollments({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
        });
        const enrollments = get(response, 'data.results', []);
        this.pathways = enrollments.map(e => e.pathway).filter(Boolean);
        this.loadProgramCounts(this.pathways);
        this.enrolledIds = new Set(this.pathways.map(p => p.id));
        this.currentPage = pageNum;
        this.totalPathways = get(response, 'data.count', 0);
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + this.pathways.length - 1;
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchPathways(pageNum);
    },

    onSearchInput(value) {
      this.searchTerm = value;
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchPathways();
    }, 400),

    viewPathway(pathway) {
      this.$router.push({ name: 'student-pathway-detail', params: { id: pathway.id } });
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.fetchUniversityOptions();
    await this.fetchEnrolledIds();
    // Setting the list triggers the watcher, which performs the first fetch.
    // Students with enrollments land on My Pathways; everyone else on the full list.
    this.selectedList = this.$route.query.list || (this.enrolledIds.size ? 'mine' : 'all');
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
